import { z } from 'zod';
import {
  objectId,
  optionalObjectId,
  moneySchema,
  positiveMoney,
  nonNegativeMoney,
  bps,
  percentBps,
  qtyMilli,
  isoDate,
  boolish,
  localizedName,
  listQueryBase,
  paginationQuery,
  currencyCode,
  attachmentRefs,
  costObjectSchema,
  addressSchema,
  contactSchema,
  bankSchema,
  workflowActionSchema,
  idParam,
} from './common.js';
import * as C from '../constants/index.js';
import { ALL_PERMISSIONS } from '../permissions.js';
import { SECTION_MODULE_KEYS } from '../modules.js';

export * from './common.js';

const enumOf = (obj) => z.enum(Object.values(obj));

// ---------------------------------------------------------------------------
// Auth & identity
// ---------------------------------------------------------------------------

export const loginSchema = z.object({
  email: z.string().email().max(200).toLowerCase(),
  password: z.string().min(1).max(200),
  totp: z
    .string()
    .regex(/^\d{6}$/)
    .optional(),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z
    .string()
    .min(10)
    .max(200)
    .regex(/[a-z]/, 'must contain a lowercase letter')
    .regex(/[A-Z]/, 'must contain an uppercase letter')
    .regex(/\d/, 'must contain a digit'),
});

export const createUserSchema = localizedName.extend({
  email: z.string().email().max(200).toLowerCase(),
  password: changePasswordSchema.shape.newPassword,
  phone: z.string().trim().max(40).optional(),
  locale: z.enum(C.LOCALES).default('en'),
  isSuperAdmin: z.boolean().default(false),
  partyId: optionalObjectId,
  assignments: z
    .array(
      z.object({
        sectionId: optionalObjectId,
        roleId: objectId,
        isDefault: z.boolean().default(false),
      }),
    )
    .default([]),
});

export const updateUserSchema = createUserSchema
  .omit({ password: true, email: true, assignments: true })
  .partial()
  .extend({ status: z.enum(['active', 'suspended', 'invited']).optional() });

export const assignSectionSchema = z.object({
  sectionId: optionalObjectId,
  roleId: objectId,
  isDefault: z.boolean().default(false),
});

export const createRoleSchema = localizedName.extend({
  key: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_]*$/, 'lowercase snake_case')
    .max(50),
  description: z.string().trim().max(500).optional(),
  scopeType: z.enum(['global', 'section']).default('section'),
  permissions: z.array(z.enum(ALL_PERMISSIONS)).min(1),
});

export const updateRoleSchema = createRoleSchema.omit({ key: true }).partial();

export const updateSectionSchema = localizedName.partial().extend({
  color: z.string().trim().max(30).optional(),
  isActive: z.boolean().optional(),
  defaultAccounts: z.record(z.string(), optionalObjectId).optional(),
  numbering: z
    .record(
      z.string(),
      z.object({
        prefix: z.string().trim().max(12),
        padding: z.number().int().min(1).max(10).default(5),
        resetPolicy: z.enum(['never', 'yearly', 'monthly']).default('yearly'),
      }),
    )
    .optional(),
});

/**
 * Which modules a section runs (docs/03 §2a).
 *
 * The whole list is replaced, never patched key by key — a partial update of a set has
 * no honest meaning ("modules: ['payroll']" could mean *only* payroll or *also* payroll)
 * and the difference is a module silently switching off. Core modules are accepted and
 * ignored rather than rejected, so a UI can send back exactly what it rendered.
 */
/**
 * The two internal controls (docs/03 §3a).
 *
 * Both optional, because they are independent switches and a caller that means to change
 * one should not have to restate the other — unlike the module list above, where a partial
 * update of a *set* has no honest meaning.  so a typo is a 422 rather than a
 * setting that silently did not take.
 */
export const setSectionControlsSchema = z
  .object({
    allowSelfApproval: z.boolean().optional(),
    requireExpenseReceipt: z.boolean().optional(),
  })
  .strict();

export const setSectionModulesSchema = z.object({
  modules: z.array(z.enum(SECTION_MODULE_KEYS)).max(SECTION_MODULE_KEYS.length),
});

// ---------------------------------------------------------------------------
// Currencies
// ---------------------------------------------------------------------------

/**
 * A rate in micro-units — `rateMicro / 1_000_000` base units buy one unit of this
 * currency, the same scale `money.convert()` takes (`FX_SCALE`).
 *
 * The ceiling is a thousand base units per unit and the floor is one micro-unit, which
 * together cover every real pair without letting a typo store a rate that would overflow
 * `Number.MAX_SAFE_INTEGER` when multiplied by an amount.
 */
export const fxRateMicro = z
  .number()
  .int()
  .min(1)
  .max(1_000 * C.FX_RATE_SCALE);

/**
 * A currency is *created*, not chosen from a hard-coded list — that is what "dynamic
 * everywhere" means here. `decimals` is part of the definition rather than looked up,
 * because it decides how an amount is parsed, stored and printed, and a currency whose
 * decimals changed after amounts were entered would silently re-scale every one of them.
 * So it is fixed at creation (`updateCurrencySchema` omits it).
 */
export const createCurrencySchema = localizedName.extend({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'a currency code is three letters, e.g. USD'),
  nameCkb: z.string().trim().max(120).optional(),
  symbol: z.string().trim().max(8).optional(),
  decimals: z.number().int().min(0).max(3).default(2),
  rateMicro: fxRateMicro.optional(),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(100),
});

export const updateCurrencySchema = createCurrencySchema
  .partial()
  .omit({ code: true, decimals: true })
  .strict();

/**
 * Re-rating is its own endpoint rather than a field on the update.
 *
 * A rate change repriced against the *live* rate every future posting, so it is an event
 * with a date and an author, not an edit — and separating it means the audit log answers
 * "who moved the dinar?" without diffing a general update.
 */
export const setCurrencyRateSchema = z.object({
  rateMicro: fxRateMicro,
  note: z.string().trim().max(200).optional(),
});

export const listCurrenciesQuery = z.object({
  includeInactive: boolish.default(false),
});

// ---------------------------------------------------------------------------
// Parties
// ---------------------------------------------------------------------------

export const employeeProfileSchema = z
  .object({
    /** Salary, commission, or both (docs/10 §5.1). Declared, never inferred from a blank. */
    compensationMode: enumOf(C.COMPENSATION_MODE).default(C.COMPENSATION_MODE.SALARY),
    jobTitle: z.string().trim().max(120).optional(),
    hireDate: isoDate.optional(),
    baseSalary: nonNegativeMoney.optional(),
    allowances: z
      .array(
        z.object({
          code: z.string().max(40),
          label: z.string().max(120).optional(),
          amount: nonNegativeMoney,
        }),
      )
      .default([]),
    deductions: z
      .array(
        z.object({
          code: z.string().max(40),
          label: z.string().max(120).optional(),
          amount: nonNegativeMoney,
        }),
      )
      .default([]),
    nationalId: z.string().trim().max(60).optional(),
    contractEndDate: isoDate.optional(),

    /**
     * **What the commission actually is** — the figure `compensationMode` implied and never
     * let anybody write down (see `Party.employee.commissionMode`).
     */
    commissionMode: enumOf(C.EMPLOYEE_COMMISSION_MODE).default(C.EMPLOYEE_COMMISSION_MODE.NONE),
    commissionAmount: nonNegativeMoney.optional(),
    /** Basis points: 250 = 2.5%. Capped at 100%, which is already an extraordinary rate. */
    commissionRateBps: z.number().int().min(0).max(10000).optional(),
  })
  /**
   * A paying mode must come with the figure it names.
   *
   * Checked here rather than left to the service, because the failure is otherwise silent
   * in the worst way: `planForEmployee()` returns null for a mode with no figure, so the
   * employee is saved looking as though they earn commission and simply never accrues any.
   * Nobody notices until a payslip is short.
   */
  .superRefine((value, ctx) => {
    if (value.commissionMode === C.EMPLOYEE_COMMISSION_MODE.PERCENT && !value.commissionRateBps) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['commissionRateBps'],
        message: 'a percentage commission needs a rate',
      });
    }
    if (value.commissionMode === C.EMPLOYEE_COMMISSION_MODE.AMOUNT && !value.commissionAmount?.amountMinor) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['commissionAmount'],
        message: 'a fixed commission needs an amount',
      });
    }
  });

export const createPartySchema = localizedName.extend({
  code: z.string().trim().max(40).optional(),
  roles: z.array(enumOf(C.PARTY_ROLE)).min(1),
  sectionIds: z.array(objectId).default([]),
  taxId: z.string().trim().max(60).optional(),
  phone: z.string().trim().max(40).optional(),
  email: z.string().email().max(200).optional().or(z.literal('')),
  contacts: z.array(contactSchema).default([]),
  address: addressSchema,
  bank: bankSchema,
  creditLimit: nonNegativeMoney.optional(),
  paymentTermsDays: z.number().int().min(0).max(365).default(0),
  defaultCommissionPlanId: optionalObjectId,
  employee: employeeProfileSchema.optional(),
  notes: z.string().trim().max(2000).optional(),
  isActive: z.boolean().default(true),
});

export const updatePartySchema = createPartySchema.partial();

export const listPartiesQuery = listQueryBase.extend({
  role: enumOf(C.PARTY_ROLE).optional(),
  isActive: boolish.optional(),
});

// ---------------------------------------------------------------------------
// Accounting
// ---------------------------------------------------------------------------

export const createAccountSchema = localizedName.extend({
  code: z
    .string()
    .trim()
    .regex(/^[0-9A-Za-z.-]{2,20}$/)
    .max(20),
  type: enumOf(C.ACCOUNT_TYPE),
  subType: z.enum(C.ACCOUNT_SUBTYPE).optional(),
  parentId: optionalObjectId,
  sectionId: optionalObjectId,
  currency: currencyCode.optional(),
  isCashAccount: z.boolean().default(false),
  /**
   * Deliberately no `isLeaf`: it is derived. An account is a leaf until something is
   * created beneath it, and accepting it here would let a caller declare a parent
   * postable, which is the one thing the flag exists to prevent.
   */
  isActive: z.boolean().default(true),
  openingBalance: moneySchema.optional(),
  description: z.string().trim().max(500).optional(),
});

export const updateAccountSchema = createAccountSchema.partial().omit({ code: true });

export const listAccountsQuery = listQueryBase.extend({
  type: enumOf(C.ACCOUNT_TYPE).optional(),
  isActive: boolish.optional(),
  /** Only leaves that are still active — what an account picker should offer. */
  postableOnly: boolish.default(false),
});

export const journalLineSchema = z
  .object({
    accountId: objectId,
    debit: nonNegativeMoney,
    credit: nonNegativeMoney,
    sectionId: optionalObjectId,
    safeId: optionalObjectId,
    partyId: optionalObjectId,
    costObject: costObjectSchema,
    description: z.string().trim().max(400).optional(),
  })
  .refine(
    (l) => l.debit.amountMinor > 0 !== l.credit.amountMinor > 0,
    'exactly one of debit or credit must be greater than zero',
  );

export const createJournalEntrySchema = z.object({
  date: isoDate,
  sectionId: optionalObjectId,
  memo: z.string().trim().max(500).optional(),
  memoAr: z.string().trim().max(500).optional(),
  currency: currencyCode.optional(),
  fxRateMicro: z.number().int().positive().optional(),
  lines: z.array(journalLineSchema).min(2, 'a journal entry needs at least two lines'),
  attachments: attachmentRefs,
});

export const listJournalQuery = listQueryBase.extend({
  accountId: optionalObjectId,
  sourceType: enumOf(C.JOURNAL_SOURCE).optional(),
  sourceId: optionalObjectId,
});

export const createFiscalYearSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  startDate: isoDate,
  endDate: isoDate,
  generatePeriods: z.boolean().default(true),
});

export const periodActionSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

export const listPeriodsQuery = paginationQuery.extend({
  fiscalYearId: optionalObjectId,
  status: enumOf(C.PERIOD_STATUS).optional(),
});

/** Reversal payload — `date` lets the caller steer which period absorbs the correction. */
export const reverseEntrySchema = z.object({
  date: isoDate.optional(),
  note: z.string().trim().max(500).optional(),
});

// ---------------------------------------------------------------------------
// Treasury
// ---------------------------------------------------------------------------

export const createSafeSchema = localizedName.extend({
  code: z.string().trim().max(20),
  type: enumOf(C.SAFE_TYPE),
  sectionId: optionalObjectId,
  /**
   * The safe's **primary** currency: what its statement opens in and what an amount
   * defaults to. It is fixed for the life of the safe.
   */
  currency: currencyCode,
  /**
   * Every currency this safe may hold, primary included (docs/02 §3).
   *
   * A safe is a box, and a box in Baghdad holds dollars and dinars at the same time. Each
   * currency gets its own wallet with its own balance and its own overdraft check —
   * summing them would be adding dinars to dollars, which is the one thing an accounting
   * system must never do. The primary is added automatically, so `[]` means "this one
   * currency only".
   */
  currencies: z.array(currencyCode).max(12).optional(),
  glAccountId: optionalObjectId,
  custodianId: optionalObjectId,
  openingBalance: moneySchema.optional(),
  allowNegative: z.boolean().default(false),
  warningThreshold: nonNegativeMoney.optional(),
});

export const updateSafeSchema = createSafeSchema.partial().omit({ type: true, currency: true });

/**
 * A direct cash adjustment against a safe — the door cash uses to enter or leave the
 * group without a customer or supplier on the other side: opening balances, capital
 * injections, bank drawings, counted corrections.
 *
 * `counterAccountId` is mandatory and unguessable by the server: cash appearing from
 * nowhere is exactly what double entry exists to prevent, so the caller must say what it
 * came from.
 */
export const adjustSafeSchema = z.object({
  direction: enumOf(C.SAFE_DIRECTION),
  amount: positiveMoney,
  counterAccountId: objectId,
  date: isoDate.optional(),
  sourceType: z.enum([C.JOURNAL_SOURCE.OPENING, C.JOURNAL_SOURCE.ADJUSTMENT]).default('adjustment'),
  description: z.string().trim().max(400).optional(),
  attachments: attachmentRefs,
});

export const listSafesQuery = listQueryBase.extend({
  type: enumOf(C.SAFE_TYPE).optional(),
  isActive: boolish.optional(),
});

export const listReconciliationsQuery = listQueryBase.extend({
  safeId: optionalObjectId,
});

export const listSafeTransactionsQuery = listQueryBase.extend({
  safeId: optionalObjectId,
  direction: enumOf(C.SAFE_DIRECTION).optional(),
  sourceType: z.string().max(40).optional(),
});

export const createReconciliationSchema = z.object({
  safeId: objectId,
  date: isoDate,
  countedBalance: nonNegativeMoney,
  denominations: z
    .array(z.object({ value: z.number().int().positive(), count: z.number().int().min(0) }))
    .default([]),
  explanation: z.string().trim().max(2000).optional(),
  attachments: attachmentRefs,
});

// ---------------------------------------------------------------------------
// Invoices & payments
// ---------------------------------------------------------------------------

/**
 * **No `taxBps`.** The group neither charges output tax nor reclaims input tax, so every
 * line carried a rate of zero and every screen showed the same figure twice. The stored
 * fields survive on the model, because a document posted before that decision keeps what it
 * posted with (**I5**) — what is gone is the ability to enter a new one.
 */
export const invoiceLineSchema = z.object({
  description: z.string().trim().min(1).max(400),
  descriptionAr: z.string().trim().max(400).optional(),
  qtyMilli: qtyMilli.default(1000),
  unitPrice: moneySchema,
  discountBps: percentBps.default(0),
  glAccountId: optionalObjectId,
  costObject: costObjectSchema,
});

export const createInvoiceSchema = z.object({
  kind: enumOf(C.INVOICE_KIND),

  /**
   * What it is *for* — rent, a deposit, a commission, a collection, a payment out.
   *
   * Optional here and required by the **service** for the sections that report on it
   * (`SECTIONS_REQUIRING_INVOICE_TYPE`), because whether a type is mandatory is a fact
   * about the section, and this schema does not know which one `sectionId` names.
   */
  type: enumOf(C.INVOICE_TYPE).optional(),
  sectionId: objectId,

  /**
   * Who it is to or from — an existing party, **or** a name to create one from.
   *
   * Unlike a treat's counterparty, this one cannot be a bare string: an invoice raises a
   * receivable or a payable, and the customer statement, the aging report and the ledger all
   * key on the party. So a typed name creates the record rather than replacing it, in the
   * same transaction as the invoice — the operator types, and the books still have somebody
   * to point at.
   */
  partyId: optionalObjectId,
  party: localizedName
    .extend({
      phone: z.string().trim().max(40).optional(),
    })
    .optional(),

  /**
   * Which employees earned it.
   *
   * A list, because a sale is often closed by more than one person and naming one of them
   * used to mean settling the other in conversation. Each accrues their **own** commission
   * from their own terms, so this is not a split of one entitlement — see
   * `Invoice.earnedByPartyIds`.
   *
   * Deduplicated rather than refused: the same name picked twice is a slip in a multi-select,
   * not an intent to pay somebody double, and `accrueForEmployee` is idempotent per person
   * anyway. Silently correcting it is the honest behaviour here — there is exactly one thing
   * the operator can have meant.
   */
  earnedByPartyIds: z
    .array(objectId)
    .max(10)
    .transform((ids) => [...new Set(ids)])
    .optional(),
  date: isoDate,
  issueDate: isoDate.optional(),
  dueDate: isoDate.optional(),
  currency: currencyCode,
  fxRateMicro: z.number().int().positive().optional(),
  lines: z.array(invoiceLineSchema).min(1, 'an invoice needs at least one line'),
  sourceRef: z
    .object({
      type: z.enum([
        'saleContract',
        'lease',
        'deal',
        'project',
        'serviceContract',
        'certificate',
        'installment',
        'none',
      ]),
      id: optionalObjectId,
    })
    .nullish(),
  commissionPlanId: optionalObjectId,
  recurrence: z
    .object({
      enabled: z.boolean().default(false),
      frequency: z.enum(['weekly', 'monthly', 'quarterly', 'semiannual', 'annual']).optional(),
      nextRunAt: isoDate.optional(),
      endDate: isoDate.optional(),
    })
    .optional(),
  notes: z.string().trim().max(2000).optional(),
  terms: z.string().trim().max(2000).optional(),

  /**
   * **What a commission was earned on** (docs/10 §26) — subject, area, property, deal date.
   *
   * Every field optional, and free text rather than a link to a property: the commission is
   * usually on a sale the group only brokered, so the property has no record here and a
   * picker would refuse the ordinary case. See `Invoice.commissionDetail`.
   */
  commissionDetail: z
    .object({
      subject: z.string().trim().max(400).optional(),
      area: z.string().trim().max(60).optional(),
      propertyRef: z.string().trim().max(200).optional(),
      earnedOn: isoDate.optional(),
      /**
       * **The deal the commission was taken on**, as money (docs/10 §45). The invoice's
       * amount is what the group owes the person; this is what the sale was worth, and the
       * difference is the company's. It used to survive only as a phrase on the line.
       */
      basis: moneySchema.optional(),
    })
    .optional(),

  attachments: attachmentRefs,

  /**
   * **Save it straight into the ledger**, rather than leaving a draft for somebody to walk
   * through submit → approve → post.
   *
   * The two-step lifecycle (docs/02 §10) exists because approving somebody else's document
   * is a control. In an office where the person entering the invoice *is* the person who
   * stands behind it, that walk is three clicks that always end the same way — and a draft
   * that nobody remembers to post is worse than no control at all, because the register
   * shows the document while the customer's balance, the aging report and the commission
   * all quietly ignore it.
   *
   * So this is opt-in, per document, and it is **not** a way around the permission ladder:
   * the service refuses it without `invoice.post`, which `section_accountant` does not hold.
   * What it does bypass is segregation of duties, deliberately and visibly — one act by one
   * person, recorded as such in the approval trail and in the audit log (**I10**).
   *
   * Everything else still applies. A closed period still refuses the entry (**I6**), the
   * commission still accrues, and the result is immutable the moment it lands (**I5**) —
   * which is the real cost of this flag and why it is not the default.
   */
  post: boolish.optional(),
});

export const createInvoiceBodySchema = createInvoiceSchema
  .refine((value) => !!value.partyId !== !!value.party, {
    message: 'name either an existing partyId or a new party, not both',
    path: ['partyId'],
  })
  /* A receipt is not an outcome and a payment is not an income (`INVOICE_TYPES_BY_KIND`). */
  .refine((value) => C.isInvoiceTypeAllowed(value.kind, value.type), {
    message: 'this type does not belong on an invoice of that direction',
    path: ['type'],
  });

export const updateInvoiceSchema = createInvoiceSchema
  // Re-pointing an invoice at a *new* party is a create-time convenience only, and `post`
  // is an instruction about *this* request rather than a field of the document — editing
  // one into the ledger is what `POST /invoices/:id/post` is for.
  .omit({ kind: true, sectionId: true, party: true, post: true })
  .partial();

export const listInvoicesQuery = listQueryBase.extend({
  kind: enumOf(C.INVOICE_KIND).optional(),
  type: enumOf(C.INVOICE_TYPE).optional(),
  partyId: optionalObjectId,
  /**
   * Every invoice one person is on, either way: addressed to them, **or** naming them as an
   * earner (docs/10 §46.8). `partyId` alone is the first relationship only, which for a
   * salesperson is almost none of their invoices.
   */
  involvedPartyId: optionalObjectId,
  overdueOnly: boolish.optional(),
  /** Super admin only: the invoices the override removed, so one can be found and restored. */
  deleted: boolish.optional(),
});

export const paymentAllocationSchema = z.object({
  invoiceId: objectId,
  amount: positiveMoney,
});

export const createPaymentSchema = z
  .object({
    kind: enumOf(C.PAYMENT_KIND),
    sectionId: objectId,
    partyId: objectId,
    date: isoDate,
    amount: positiveMoney,
    currency: currencyCode,
    fxRateMicro: z.number().int().positive().optional(),
    method: enumOf(C.PAYMENT_METHOD),
    safeId: optionalObjectId,
    reference: z.string().trim().max(120).optional(),
    allocations: z.array(paymentAllocationSchema).default([]),
    notes: z.string().trim().max(1000).optional(),
    attachments: attachmentRefs,
  })
  .refine((p) => p.method !== C.PAYMENT_METHOD.CASH || !!p.safeId, {
    message: 'cash payments require a safe',
    path: ['safeId'],
  });

export const listPaymentsQuery = listQueryBase.extend({
  kind: enumOf(C.PAYMENT_KIND).optional(),
  partyId: optionalObjectId,
  safeId: optionalObjectId,
});

// ---------------------------------------------------------------------------
// Expenses
// ---------------------------------------------------------------------------

/**
 * Kept as a plain object so `updateExpenseSchema` can still call `.partial()`:
 * `.refine()` returns a ZodEffects, which has none of the object methods.
 */
const expenseBase = z.object({
  sectionId: objectId,
  date: isoDate,
  /**
   * **Optional, and filled from the section when it is left out.**
   *
   * Every section carries an `expenseDefault` — its own Operating Expenses leaf, written
   * on by the chart seed — so “which category?” has a configured answer and does not need
   * to be a required field on a form somebody fills in twenty times a day. The service
   * resolves it; re-categorising afterwards is one click.
   */
  categoryAccountId: optionalObjectId,
  partyId: optionalObjectId,
  /**
   * **Or the supplier this expense brings with it** — a name typed on the form.
   *
   * The same convenience `createInvoiceSchema` offers, and needed here for a sharper
   * reason: the supplier of a petty-cash purchase is very often being met for the first
   * time, so a closed dropdown made "who was this paid to?" answerable only by leaving the
   * form, registering the vendor, and starting again. The service creates the party inside
   * the expense's own transaction, so a refused save leaves nobody half-entered.
   */
  party: localizedName
    .extend({
      phone: z.string().trim().max(40).optional(),
    })
    .optional(),
  /**
   * The employee who actually spent it — not the vendor it was paid to (`partyId`).
   * Feeds the company expenses register's "who did this?" column and the payroll summary,
   * where what somebody spent out of pocket is added to what they are paid. See the model.
   */
  spentByPartyId: optionalObjectId,
  /**
   * **Optional, because a petty-cash slip has already said it.**
   *
   * The category names what was bought, the supplier names who was paid and the receipt is
   * attached to the row — so a mandatory free-text line was a fourth answer to a question
   * already answered three times, and what it actually produced was “-” and “expense”
   * typed a hundred times to get past a required field.
   *
   * The register and the journal memo fall back to the category name where it is blank, so
   * nothing downstream renders an empty cell.
   */
  description: z.string().trim().max(500).optional(),
  amount: positiveMoney,
  currency: currencyCode,
  fxRateMicro: z.number().int().positive().optional(),
  paymentMode: enumOf(C.EXPENSE_PAYMENT_MODE),
  safeId: optionalObjectId,
  costObject: costObjectSchema,
  attachments: attachmentRefs,
  notes: z.string().trim().max(1000).optional(),
});

/**
 * Paying cash out of a safe means naming the safe it left — **or leaving it to the section**.
 *
 * This used to be a hard refusal here, which made `safeId` a required field on every cash
 * expense. The section already points at the box it spends from (`section.safeId`, set by
 * the seed and the same one for all four), so the service fills it and only fails when the
 * section genuinely has none — by which point it can say so, which a schema refusal on a
 * field the operator never filled in could not.
 *
 * The rule itself has not moved: a cash expense still cannot post without a safe.
 */
export const createExpenseSchema = expenseBase
  /* Name somebody on file *or* somebody to create — naming both is two different answers
     to one question, and the service would have to pick one silently. */
  .refine((e) => !(e.partyId && e.party), {
    message: 'name either an existing partyId or a new party, not both',
    path: ['partyId'],
  });

export const updateExpenseSchema = expenseBase
  // Re-pointing an expense at a *new* supplier is a create-time convenience only.
  .omit({ sectionId: true, party: true })
  .partial();

export const listExpensesQuery = listQueryBase.extend({
  categoryAccountId: optionalObjectId,
  paymentMode: enumOf(C.EXPENSE_PAYMENT_MODE).optional(),
  /** "What has Aram spent this month?" — the payroll summary asks exactly this. */
  spentByPartyId: optionalObjectId,
});

// ---------------------------------------------------------------------------
// Attachments (invariant I11 — files never stream through the API)
// ---------------------------------------------------------------------------

/**
 * The client declares what it is about to upload; the server validates the mime type
 * and size *before* handing back a presigned URL, so an oversized or disallowed file is
 * refused without a byte reaching the bucket.
 *
 * `sizeBytes` is the client's claim. It is re-checked against the real object with
 * HeadObject at confirm time, because a presigned PUT can be replayed with a different
 * body.
 */
export const presignAttachmentSchema = z.object({
  ownerType: z.enum(C.ATTACHMENT_OWNER_TYPES),
  /** Absent for a file uploaded before its document exists — attached on confirm. */
  ownerId: optionalObjectId,
  sectionId: optionalObjectId,
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.enum(C.ALLOWED_MIME_TYPES),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(C.MAX_ATTACHMENT_BYTES, `files are limited to ${C.MAX_ATTACHMENT_BYTES} bytes`),
  checksumSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/i)
    .optional(),
  tags: z.array(z.string().trim().max(40)).max(10).default([]),
});

export const confirmAttachmentSchema = z.object({
  /** Lets a file uploaded before its document existed be bound to it afterwards. */
  ownerId: optionalObjectId,
  etag: z.string().trim().max(120).optional(),
});

export const listAttachmentsQuery = listQueryBase.extend({
  ownerType: z.enum(C.ATTACHMENT_OWNER_TYPES).optional(),
  ownerId: optionalObjectId,
  status: enumOf(C.ATTACHMENT_STATUS).optional(),
});

export const attachmentUrlQuery = z.object({
  /** `inline` previews in the browser; the default downloads. */
  inline: boolish.default(false),
});

/**
 * Previews for a whole page of documents at once.
 *
 * A POST rather than a GET because the ids are a page of results — forty of them in a
 * query string is a URL long enough to meet proxy limits, and this reads nothing the
 * caller could not already read.
 *
 * The cap is the page-size ceiling: a caller asking for more has misunderstood the
 * endpoint, and silently truncating would show a paperclip on some rows and not others
 * with no way to tell why.
 */
export const attachmentPreviewsSchema = z.object({
  ownerType: z.enum(C.ATTACHMENT_OWNER_TYPES),
  ownerIds: z.array(objectId).min(1).max(C.MAX_PAGE_SIZE),
});

// ---------------------------------------------------------------------------
// Payroll
// ---------------------------------------------------------------------------

const periodMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'expected YYYY-MM');

/**
 * There is no "create a payroll run" — a run is **generated** from the employees' stored
 * salary structures. A run somebody typed by hand is a run nobody can reconcile to a
 * contract, so this is the only door in.
 *
 * `payDate` is optional: omitted, the service uses the last day of the month, which is the
 * conventional pay date and saves the caller computing February.
 */
export const generatePayrollSchema = z.object({
  sectionId: objectId,
  periodMonth,
  payDate: isoDate.optional(),
  currency: currencyCode.optional(),
  notes: z.string().trim().max(2000).optional(),
});

export const updatePayslipSchema = z.object({
  overtime: nonNegativeMoney.optional(),
  allowances: z.array(z.object({ code: z.string().max(40), amount: nonNegativeMoney })).optional(),
  deductions: z.array(z.object({ code: z.string().max(40), amount: nonNegativeMoney })).optional(),
  advancesRecovered: nonNegativeMoney.optional(),
  note: z.string().trim().max(500).optional(),
});

/**
 * `employeePartyIds` pays a subset — a run where one bank transfer bounced should not have
 * to be paid all over again. Omitted, it pays everyone still unpaid.
 */
export const payPayrollSchema = z.object({
  safeId: objectId,
  employeePartyIds: z.array(objectId).optional(),
  date: isoDate.optional(),
  notes: z.string().trim().max(1000).optional(),
});

export const listPayrollQuery = listQueryBase.extend({
  periodMonth: periodMonth.optional(),
});

/**
 * The payroll summary and the employee KPI sheet — one window, one currency.
 *
 * `currency` is **required**, unlike most report queries, and for the reason the customer
 * and owner statements require it: a salary in dinars, a commission in dollars and a fuel
 * receipt in dinars do not add up to anything (**I13**), and defaulting would silently pick
 * one and present the result as the whole of what somebody is owed.
 *
 * `periodMonth` and the date range are both offered because the two reports are read two
 * ways — "what do we pay this month" and "how did the quarter go" — and the service prefers
 * the month where both arrive.
 */
export const staffReportQuery = z.object({
  sectionId: optionalObjectId,
  partyId: optionalObjectId,
  periodMonth: periodMonth.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  currency: currencyCode,
  /** Somebody who left mid-month is still owed for it, so the list can be asked for them. */
  includeInactive: boolish.optional(),
});

/**
 * **The Employee Report** — one person, everything the group did with them.
 *
 * `partyId` is required where `staffReportQuery` leaves it optional, and that is the whole
 * difference between the two: the list is a roster that may be narrowed to somebody, this is
 * a report *about* somebody and has no meaning without one. `currency` is required for the
 * reason every statement's is (I13) — a salary in dinars and a commission in dollars do not
 * add up, so the caller names which set of books they are asking about rather than having
 * one quietly picked for them.
 */
export const employeeReportQuery = z.object({
  partyId: objectId,
  /**
   * **Optional, and omitting it means the currency this person is paid in.**
   *
   * It was required, and the screen defaulted it to the group's base — so opening somebody
   * on a dinar salary assembled their entire report in dollars and every figure came back
   * zero. Correct under I13 and an answer to a question nobody asked.
   *
   * Passing one is still honoured: reading a dinar-paid employee in dollars is a legitimate
   * question and those zeros are its answer. It is simply no longer the default.
   */
  currency: currencyCode.optional(),
  /**
   * **Read it in every configured currency at once.**
   *
   * The third answer to a picker that offered USD *or* IQD, for the employee paid a dinar
   * salary who earns commission on dollar invoices and is half invisible either way.
   *
   * Wins over `currency` where both arrive — it is the more specific request, and a screen
   * sending both has already confused itself. Nothing is converted and nothing is summed
   * across currencies: the lists merge, the totals stay one set per currency (**I13**).
   */
  allCurrencies: boolish.optional(),
  periodMonth: periodMonth.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
});

/**
 * **The staff book** — every employee's own report, one after another.
 *
 * The one report query with **no `currency`**, and that is the point of it rather than an
 * omission. Each page is assembled in the currency that person is actually paid in, read off
 * their own terms, so there is nothing for the caller to choose: asking would only let them
 * choose wrongly and get a book of zeros, which is the screen this replaces.
 *
 * The totals it does state are grouped by currency, never summed across them (**I13**).
 */
export const employeeBookQuery = z.object({
  sectionId: optionalObjectId,
  periodMonth: periodMonth.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  /** Somebody who left mid-month is still owed for it, so the book can be asked for them. */
  includeInactive: boolish.optional(),
});

export const salaryRegisterQuery = z.object({
  sectionId: optionalObjectId,
  periodMonth: periodMonth.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  status: z.union([z.string(), z.array(z.string())]).optional(),
});

// ---------------------------------------------------------------------------
// Commissions
// ---------------------------------------------------------------------------

export const commissionTierSchema = z.object({
  fromMinor: z.number().int().min(0),
  toMinor: z.number().int().positive().nullable(),
  method: z.enum([C.COMMISSION_METHOD.PERCENT, C.COMMISSION_METHOD.FIXED]),
  rateBps: bps.optional(),
  fixedAmount: nonNegativeMoney.optional(),
});

export const commissionConditionSchema = z.object({
  field: z.string().trim().min(1).max(80),
  op: z.enum(C.CONDITION_OPS),
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))]),
});

export const commissionSplitSchema = z.object({
  beneficiary: enumOf(C.BENEFICIARY_ROLE),
  partyId: optionalObjectId,
  percentBps: percentBps,
});

/**
 * The plan's own fields, before the cross-field rules.
 *
 * Kept as a bare object so a *new version* can reuse it minus `code` — a version
 * inherits its group's code and must not be able to rename itself halfway through a
 * history (**I9**: a snapshot is only readable if the code still means what it meant).
 */
/**
 * **Which sources carry a cost, and therefore a profit to compute on.**
 *
 * An invoice knows what it charged, not what it cost — `commissionContextOf` supplies
 * `grossAmount` and `collectedAmount` and no profit figure at all — so a plan computing on
 * `net_profit` cannot price one.
 *
 * **This is no longer a refusal.** It briefly was, and that was the wrong shape twice over.
 * A plan applying to `['deal', 'invoice.income']` and computing on profit is a perfectly
 * ordinary arrangement — it prices the deals and simply does not govern the invoices — and
 * the rule refused it outright. Meanwhile the situation it was meant to prevent had already
 * been fixed properly one layer down: `resolve.js` treats a plan the document cannot feed as
 * *ineligible*, so it is skipped like any other mismatch instead of taking the posting with
 * it (see the note there).
 *
 * The list stays because `plans.doctor.js` reads the same idea to report plans that will
 * never pay out on some of what they claim to cover. Reporting it is right; refusing to save
 * it was not.
 */
export const SOURCES_WITH_PROFIT = ['deal'];

export const commissionPlanFields = localizedName.extend({
  code: z.string().trim().max(40),
  description: z.string().trim().max(500).optional(),
  sectionId: optionalObjectId,
  appliesTo: z
    .array(
      z.enum([
        'invoice.income',
        'invoice.outcome',
        'deal',
        'saleContract',
        'lease',
        'project',
        'serviceContract',
        'certificate',
      ]),
    )
    .min(1),
  base: enumOf(C.COMMISSION_BASE),
  customBaseField: z.string().trim().max(80).optional(),
  method: enumOf(C.COMMISSION_METHOD),
  rateBps: bps.optional(),
  fixedAmount: nonNegativeMoney.optional(),
  tiers: z.array(commissionTierSchema).default([]),
  conditions: z.array(commissionConditionSchema).default([]),
  splits: z.array(commissionSplitSchema).min(1),
  accrualTrigger: enumOf(C.COMMISSION_TRIGGER).default(C.COMMISSION_TRIGGER.ON_POST),
  payoutTrigger: enumOf(C.COMMISSION_PAYOUT_TRIGGER).default(C.COMMISSION_PAYOUT_TRIGGER.MANUAL),
  capMin: nonNegativeMoney.optional(),
  capMax: nonNegativeMoney.optional(),
  rounding: z.enum(['half_up', 'half_even', 'down', 'up']).default('half_up'),
  priority: z.number().int().min(0).max(1000).default(100),
  effectiveFrom: isoDate,
  effectiveTo: isoDate.nullish(),
  isActive: z.boolean().default(true),
});

/** The cross-field rules, applied to whichever shape is being validated. */
const withPlanRules = (schema) =>
  schema
    .refine((p) => p.splits.reduce((s, x) => s + x.percentBps, 0) === 10000, {
      message: 'commission splits must sum to exactly 100% (10000 bps)',
      path: ['splits'],
    })
    .refine((p) => p.method !== C.COMMISSION_METHOD.PERCENT || typeof p.rateBps === 'number', {
      message: 'percent plans require rateBps',
      path: ['rateBps'],
    })
    .refine((p) => p.method !== C.COMMISSION_METHOD.FIXED || !!p.fixedAmount, {
      message: 'fixed plans require fixedAmount',
      path: ['fixedAmount'],
    })
    .refine(
      (p) =>
        ![C.COMMISSION_METHOD.TIERED, C.COMMISSION_METHOD.PROGRESSIVE].includes(p.method) ||
        p.tiers.length > 0,
      { message: 'tiered plans require at least one tier', path: ['tiers'] },
    )
    .refine((p) => p.base !== C.COMMISSION_BASE.CUSTOM || !!p.customBaseField, {
      message: 'a custom base must name the field it reads',
      path: ['customBaseField'],
    })
    .refine((p) => !p.capMin || !p.capMax || p.capMin.amountMinor <= p.capMax.amountMinor, {
      message: 'capMin cannot exceed capMax',
      path: ['capMin'],
    })
    .refine((p) => !p.effectiveTo || p.effectiveFrom <= p.effectiveTo, {
      message: 'effectiveFrom must be on or before effectiveTo',
      path: ['effectiveTo'],
    });

export const createCommissionPlanSchema = withPlanRules(commissionPlanFields);

/** A new version inherits `code` from its group, so the body must not carry one. */
export const newCommissionPlanVersionSchema = withPlanRules(commissionPlanFields.omit({ code: true }));

/**
 * In-place edits are limited to what cannot change an amount.
 *
 * Anything the engine reads — base, method, rates, tiers, splits, caps, rounding — is
 * absent here on purpose: changing it creates a new version instead (**I9**). `priority`
 * is allowed because it selects *which* plan applies to future documents and never
 * re-prices a computed one.
 */
export const updateCommissionPlanSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    nameAr: z.string().trim().max(200).optional(),
    nameCkb: z.string().trim().max(200).optional(),
    description: z.string().trim().max(500).optional(),
    priority: z.number().int().min(0).max(1000).optional(),
  })
  /*
   * `.strict()`, not Zod's default strip. A `PATCH { rateBps: 5000 }` that silently drops
   * the field would answer 200 and leave the rate untouched — the caller walks away
   * believing they repriced the plan. Rejecting the key says what is actually true: the
   * arithmetic is only changed by superseding (**I9**).
   */
  .strict()
  .refine((b) => Object.keys(b).length > 0, { message: 'nothing to update' });

export const testCommissionPlanSchema = z.object({
  baseAmount: positiveMoney,
  context: z.record(z.string(), z.unknown()).default({}),
});

/** Same as `test`, but for a plan the builder has not saved yet — the live preview. */
export const previewCommissionPlanSchema = testCommissionPlanSchema.extend({
  plan: createCommissionPlanSchema,
});

export const listCommissionPlansQuery = listQueryBase.extend({
  appliesTo: z.string().max(40).optional(),
  planGroupId: optionalObjectId,
  isActive: boolish.optional(),
  /** Hide superseded versions — the default view of a plan list. */
  latestOnly: boolish.default(false),
});

export const listCommissionsQuery = listQueryBase.extend({
  beneficiaryPartyId: optionalObjectId,
  planId: optionalObjectId,
  sourceType: z.string().max(40).optional(),
});

export const payoutCommissionsSchema = z.object({
  commissionIds: z.array(objectId).min(1),
  safeId: objectId,
  date: isoDate.optional(),
  notes: z.string().trim().max(1000).optional(),
});

export const commissionStatementQuery = z.object({
  beneficiaryPartyId: objectId,
  sectionId: optionalObjectId,
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  status: z.union([z.string(), z.array(z.string())]).optional(),
});

// ---------------------------------------------------------------------------
// Profit
// ---------------------------------------------------------------------------

export const computeProfitSchema = z.object({
  sectionId: objectId,
  periodId: objectId,
});

export const distributeProfitSchema = z
  .object({
    profitStatementId: objectId,
    distributableAmount: positiveMoney.optional(),
    shares: z
      .array(
        z.object({
          partyId: optionalObjectId,
          role: z.enum(['company', 'broker', 'partner', 'investor']),
          percentBps: percentBps,
        }),
      )
      .min(1),
  })
  .refine((d) => d.shares.reduce((s, x) => s + x.percentBps, 0) === 10000, {
    message: 'profit shares must sum to exactly 100% (10000 bps)',
    path: ['shares'],
  })
  /*
   * Every share except the company's own must name who it is owed to. A `partner` share
   * with no party is an amount posted to Partner Payable that nobody can ever be paid,
   * and it is invisible until someone asks why the account will not clear.
   */
  .refine((d) => d.shares.every((s) => s.role === C.PROFIT_SHARE_ROLE.COMPANY || !!s.partyId), {
    message: 'every share other than the company’s must name a party',
    path: ['shares'],
  });

export const listProfitStatementsQuery = listQueryBase.extend({
  periodId: optionalObjectId,
  fiscalYearId: optionalObjectId,
});

export const listDistributionsQuery = listQueryBase.extend({
  profitStatementId: optionalObjectId,
  periodId: optionalObjectId,
});

/** Pay a distribution's shares out of a safe, like a commission payout. */
export const payDistributionSchema = z.object({
  safeId: objectId,
  partyIds: z.array(objectId).optional(),
  date: isoDate.optional(),
  notes: z.string().trim().max(1000).optional(),
});

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

export const presignUploadSchema = z.object({
  ownerType: z.enum(C.ATTACHMENT_OWNER_TYPES),
  ownerId: optionalObjectId,
  sectionId: optionalObjectId,
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.enum(C.ALLOWED_MIME_TYPES),
  sizeBytes: z.number().int().positive().max(C.MAX_ATTACHMENT_BYTES),
  checksumSha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/i)
    .optional(),
  tags: z.array(z.string().max(40)).max(10).default([]),
});

export const confirmUploadSchema = z.object({
  etag: z.string().max(200).optional(),
  ownerId: optionalObjectId,
});

// ---------------------------------------------------------------------------
// Verticals
// ---------------------------------------------------------------------------

export const createPropertySchema = localizedName.extend({
  sectionId: objectId,
  code: z.string().trim().max(40).optional(),
  type: z.enum(C.PROPERTY_TYPE),
  buildingName: z.string().trim().max(150).optional(),
  unitNo: z.string().trim().max(40).optional(),
  floor: z.string().trim().max(20).optional(),
  areaSqm: z.number().min(0).max(1_000_000).optional(),
  rooms: z.number().int().min(0).max(100).optional(),
  ownerPartyId: optionalObjectId,
  /**
   * Who is running the build (docs/10 §4.1a) — typed, with the party attached only when
   * the name matches somebody on file. A site engineer is often hired for one build and
   * never reaches the payroll register, so requiring a party made registering a property
   * wait on creating an employee.
   */
  engineerName: z.string().trim().max(200).optional(),
  engineerPartyId: optionalObjectId,
  purchasePrice: nonNegativeMoney.optional(),
  listPrice: nonNegativeMoney.optional(),
  expectedRent: nonNegativeMoney.optional(),
  status: enumOf(C.PROPERTY_STATUS).default(C.PROPERTY_STATUS.AVAILABLE),
  address: addressSchema,
  notes: z.string().trim().max(2000).optional(),
  attachments: attachmentRefs,
});

// --- property owners (docs/10 §4.1a) ---------------------------------------

/**
 * **Open an owner an account.** No money moves — this is the register entry, and the
 * balance starts empty because nothing has been paid in yet.
 */
export const createOwnerAccountSchema = z.object({
  sectionId: objectId,
  partyId: objectId,
  status: enumOf(C.OWNER_STATUS).optional(),
  notes: z.string().trim().max(1000).optional(),
});

export const updateOwnerAccountSchema = z
  .object({
    status: enumOf(C.OWNER_STATUS).optional(),
    notes: z.string().trim().max(1000).optional(),
  })
  .strict();

/**
 * A deposit and a refund take the same body: whose money, which box, how much.
 *
 * `amount` is always **positive** — the direction is the endpoint, never a sign on the
 * figure. A negative deposit and a refund would be two ways to say one thing, and the
 * statement would have to guess which the operator meant.
 */
export const ownerMoneySchema = z.object({
  sectionId: objectId,
  partyId: objectId,
  safeId: objectId,
  amount: positiveMoney,
  date: isoDate.optional(),
  note: z.string().trim().max(1000).optional(),
});

/**
 * A super admin's correction to a deposit or a refund (docs/10 §47). Every field optional:
 * what is not sent stays as it was — the movement is reversed and re-posted with the merged
 * figures, so a note fixed on its own still keeps the amount, the date and the safe.
 */
export const ownerMovementAmendSchema = z.object({
  amount: positiveMoney.optional(),
  date: isoDate.optional(),
  safeId: objectId.optional(),
  note: z.string().trim().max(1000).optional(),
});

/**
 * One site, and what was spent on it (docs/10 §4.1a).
 *
 * `currency` is optional here, unlike the owner statement: a property has no running
 * balance to keep inside one set of books — it has a total per currency, and showing all of
 * them at once is the ordinary way to read a site.
 */
export const propertyStatementQuery = z.object({
  currency: currencyCode.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
});

export const listOwnerAccountsQuery = listQueryBase.extend({
  status: enumOf(C.OWNER_STATUS).optional(),
});

/**
 * One owner, one currency, every movement of his money.
 *
 * `currency` is required rather than defaulted, for the reason the customer statement
 * gives: a running balance is only meaningful inside one set of books (**I13**).
 */
export const ownerStatementQuery = z.object({
  partyId: objectId,
  currency: currencyCode,
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  q: z.string().trim().max(120).optional(),
  /** Export only: print the reversed pairs the screen folds away. The report itself always
      returns them, flagged, and leaves the choice to the reader. */
  showReversed: boolish.optional(),
});

export const createSaleContractSchema = z.object({
  sectionId: objectId,
  propertyId: objectId,
  buyerPartyId: objectId,
  sellerPartyId: optionalObjectId,
  date: isoDate,
  price: positiveMoney,
  downPayment: nonNegativeMoney.optional(),
  installmentCount: z.number().int().min(0).max(360).default(0),
  installmentFrequency: enumOf(C.LEASE_FREQUENCY).default(C.LEASE_FREQUENCY.MONTHLY),
  firstInstallmentDate: isoDate.optional(),
  commissionPlanId: optionalObjectId,
  brokerPartyId: optionalObjectId,
  notes: z.string().trim().max(2000).optional(),
  attachments: attachmentRefs,
});

export const createLeaseSchema = z
  .object({
    sectionId: objectId,
    propertyId: objectId,
    tenantPartyId: objectId,
    startDate: isoDate,
    endDate: isoDate,
    rentAmount: positiveMoney,
    frequency: enumOf(C.LEASE_FREQUENCY).default(C.LEASE_FREQUENCY.MONTHLY),
    securityDeposit: nonNegativeMoney.optional(),
    escalationBps: percentBps.default(0),
    autoInvoice: z.boolean().default(true),
    commissionPlanId: optionalObjectId,
    /**
     * Who earned the letting fee. Without this a plan paying a `broker` split has nothing
     * to resolve against and the engine refuses the accrual — the sale contract schema
     * always had it and the lease one did not, which made letting commissions unpayable.
     */
    brokerPartyId: optionalObjectId,
    currency: currencyCode.optional(),
    notes: z.string().trim().max(2000).optional(),
    attachments: attachmentRefs,
  })
  .refine((l) => l.startDate < l.endDate, { message: 'endDate must be after startDate', path: ['endDate'] });

/** `status` is absent on purpose — it is derived from the unit's contracts, not typed. */
export const updatePropertySchema = createPropertySchema
  .omit({ sectionId: true, code: true, status: true })
  .partial();

export const listPropertiesQuery = listQueryBase.extend({
  type: z.enum(C.PROPERTY_TYPE).optional(),
  ownerPartyId: optionalObjectId,
  buildingName: z.string().trim().max(150).optional(),
});

export const listSaleContractsQuery = listQueryBase.extend({
  propertyId: optionalObjectId,
  buyerPartyId: optionalObjectId,
});

export const listLeasesQuery = listQueryBase.extend({
  propertyId: optionalObjectId,
  tenantPartyId: optionalObjectId,
  /** For the renewals list: leases whose term ends on or before this date. */
  expiringBefore: isoDate.optional(),
});

export const listInstallmentsQuery = listQueryBase.extend({
  contractId: optionalObjectId,
  contractType: z.enum(['saleContract', 'lease', 'serviceContract']).optional(),
  partyId: optionalObjectId,
  propertyId: optionalObjectId,
});

/** Rent roll — what every let unit earns, and what is vacant. */
export const rentRollQuery = z.object({
  sectionId: optionalObjectId,
  buildingName: z.string().trim().max(150).optional(),
  asOf: isoDate.optional(),
});

export const occupancyQuery = z.object({
  sectionId: optionalObjectId,
  buildingName: z.string().trim().max(150).optional(),
});

/** Customers and their balances (docs/10 §5.3). Shared by Construction, Agency, Real Estate. */
export const customerListQuery = listQueryBase.extend({
  sectionId: optionalObjectId,
});

/**
 * The tenants register (docs/10 §4.2b) — the same query, and deliberately the same shape.
 *
 * A separate export rather than a `role` parameter on the one above: which register a URL
 * serves is the *route's* decision, not the caller's. A `?role=` the client could set
 * would let anybody holding `party.view` re-point the customers screen at brokers or
 * employees, which is a different list with a different meaning.
 */
export const tenantListQuery = customerListQuery;

/**
 * `currency` is required rather than defaulted.
 *
 * A statement mixes debits and credits into one running balance, which is only meaningful
 * inside a single currency (I13). Defaulting it would silently pick one and present the
 * result as *the* balance, which is the misreading §5.3 exists to prevent — so the caller
 * states which set of books they are asking about.
 */
export const customerStatementQuery = z.object({
  partyId: objectId,
  currency: currencyCode,
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
});

export const createServiceContractSchema = localizedName.extend({
  sectionId: objectId,
  clientPartyId: objectId,
  type: enumOf(C.SERVICE_CONTRACT_TYPE),
  value: positiveMoney,
  startDate: isoDate,
  endDate: isoDate.optional(),
  billingSchedule: z
    .array(
      z.object({
        dueDate: isoDate,
        amount: positiveMoney,
        label: z.string().trim().max(200).optional(),
      }),
    )
    .default([]),
  deliverables: z
    .array(
      z.object({
        title: z.string().trim().max(200),
        dueDate: isoDate.optional(),
        status: z.enum(['pending', 'in_progress', 'delivered', 'accepted']).default('pending'),
      }),
    )
    .default([]),
  commissionPlanId: optionalObjectId,
  /** Who earned it — lets the engine resolve an `agent` split from the document. */
  agentPartyId: optionalObjectId,
  currency: currencyCode.optional(),
  notes: z.string().trim().max(2000).optional(),
  attachments: attachmentRefs,
});

/** Editable while the contract is a draft; the service refuses money terms once active. */
export const updateServiceContractSchema = createServiceContractSchema.omit({ sectionId: true }).partial();

export const listServiceContractsQuery = listQueryBase.extend({
  type: enumOf(C.SERVICE_CONTRACT_TYPE).optional(),
  clientPartyId: optionalObjectId,
});

/** `acceptedAt` is absent on purpose — the service stamps it so it cannot be backdated. */
export const updateDeliverableSchema = z
  .object({
    title: z.string().trim().max(200).optional(),
    dueDate: isoDate.optional(),
    status: z.enum(['pending', 'in_progress', 'delivered', 'accepted']).optional(),
    notes: z.string().trim().max(500).optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: 'nothing to update' });

export const createCampaignSchema = localizedName.extend({
  sectionId: objectId,
  code: z.string().trim().max(40).optional(),
  contractId: optionalObjectId,
  clientPartyId: objectId,
  currency: currencyCode.optional(),
  budget: nonNegativeMoney.optional(),
  startDate: isoDate,
  endDate: isoDate.optional(),
  channels: z.array(z.string().max(60)).default([]),
  kpis: z
    .array(
      z.object({
        name: z.string().trim().max(80),
        target: z.string().trim().max(80).optional(),
        actual: z.string().trim().max(80).optional(),
      }),
    )
    .default([]),
  status: enumOf(C.CAMPAIGN_STATUS).default(C.CAMPAIGN_STATUS.PLANNED),
  notes: z.string().trim().max(2000).optional(),
});

/**
 * No `spend` field anywhere — it is the sum of the posted expenses tagged to the campaign,
 * computed on read. Accepting one would create a figure that drifts the first time an
 * expense is reversed.
 */
export const updateCampaignSchema = createCampaignSchema
  .omit({ sectionId: true, clientPartyId: true, code: true })
  .partial();

export const listCampaignsQuery = listQueryBase.extend({
  clientPartyId: optionalObjectId,
  contractId: optionalObjectId,
});

const brokerProfileFields = z.object({
  sectionId: objectId,

  /** Somebody who is already on file as a broker. Mutually exclusive with `party`. */
  partyId: optionalObjectId,

  /**
   * …or the person themselves, created with the profile in one transaction.
   *
   * The Brokers section is where a broker is added, so it has to be able to add one: with
   * only `partyId` the register could attach settings to a person and never bring one into
   * existence, and every new broker started on a screen in another section.
   */
  party: localizedName
    .extend({
      phone: z.string().trim().max(40).optional(),
      email: z.string().email().max(200).optional().or(z.literal('')),
    })
    .optional(),

  safeId: optionalObjectId,
  defaultCommissionPlanId: optionalObjectId,

  /**
   * The **company's** commission, and it is charged **on profit only** (docs/10 §3.1).
   *
   * The broker's own share is the remainder and is never stored — a treat's profit is
   * split between exactly two credits, so two independently editable percentages could be
   * saved as 30/30 and leave 40% of a realised profit with nowhere to post.
   */
  companyProfitSharePercentBps: percentBps.default(0),

  targetAmount: nonNegativeMoney.optional(),
  notes: z.string().trim().max(1000).optional(),
});

export const createBrokerProfileSchema = brokerProfileFields.refine(
  (value) => !!value.partyId !== !!value.party,
  { message: 'name either an existing partyId or a new party, not both', path: ['partyId'] },
);

export const createDealSchema = z.object({
  sectionId: objectId,
  brokerPartyId: objectId,
  title: z.string().trim().min(1).max(200),
  assetRef: z
    .object({
      type: z.enum(['property', 'vehicle', 'land', 'other']),
      id: optionalObjectId,
      label: z.string().trim().max(200).optional(),
    })
    .optional(),
  buyerPartyId: optionalObjectId,
  sellerPartyId: optionalObjectId,
  date: isoDate,
  dealValue: positiveMoney,
  costAmount: nonNegativeMoney.optional(),
  currency: currencyCode,
  commissionPlanId: optionalObjectId,
  stage: enumOf(C.DEAL_STAGE).default(C.DEAL_STAGE.LEAD),
  notes: z.string().trim().max(2000).optional(),
  attachments: attachmentRefs,
});

export const closeDealSchema = z.object({
  date: isoDate.optional(),
  generateInvoice: z.boolean().default(true),
  finalDealValue: positiveMoney.optional(),
  finalCostAmount: nonNegativeMoney.optional(),
  note: z.string().trim().max(1000).optional(),
});

export const updateBrokerProfileSchema = brokerProfileFields
  // `party` is a create-time convenience; editing the person happens on the party itself.
  .omit({ sectionId: true, partyId: true, party: true })
  .partial()
  .extend({ status: enumOf(C.BROKER_STATUS).optional() });

export const listBrokerProfilesQuery = listQueryBase.extend({
  partyId: optionalObjectId,
});

/** A deal's own fields are editable until it closes; `sectionId` never moves. */
export const updateDealSchema = createDealSchema.omit({ sectionId: true, stage: true }).partial();

/** Pipeline moves only. Closing raises documents, so it has its own endpoint. */
export const advanceDealSchema = z.object({
  stage: z.enum([C.DEAL_STAGE.LEAD, C.DEAL_STAGE.NEGOTIATION, C.DEAL_STAGE.AGREED]),
  note: z.string().trim().max(1000).optional(),
});

export const listDealsQuery = listQueryBase.extend({
  stage: z.union([z.string(), z.array(z.string())]).optional(),
  brokerPartyId: optionalObjectId,
});

export const createPayoutSchema = z.object({
  sectionId: objectId,
  brokerPartyId: objectId,
  commissionIds: z.array(objectId).min(1),
  /** Omitted ⇒ the safe on the broker's profile. */
  safeId: optionalObjectId,
  date: isoDate.optional(),
  notes: z.string().trim().max(1000).optional(),
});

export const payPayoutSchema = z.object({
  date: isoDate.optional(),
  notes: z.string().trim().max(1000).optional(),
});

export const listPayoutsQuery = listQueryBase.extend({
  brokerPartyId: optionalObjectId,
});

/**
 * Broker capital — the broker's own money (docs/10 §3).
 *
 * `safeId` is required rather than resolved: cash physically arriving is an event somebody
 * witnessed at a particular box, and with `.strict()` a typo in the field name is refused
 * instead of silently defaulting the money somewhere else.
 */
export const brokerCapitalSchema = z
  .object({
    sectionId: objectId,
    /** **Whose** money. The party id, not the profile id — see the route comment. */
    brokerPartyId: objectId,
    safeId: objectId,
    amount: positiveMoney,
    date: isoDate.optional(),
    note: z.string().trim().max(1000).optional(),
    attachments: attachmentRefs,
  })
  .strict();

/**
 * Treats — buying and selling with a broker's money (docs/10 §3.2).
 *
 * There is no `sellAmount` on the buy: a treat that has not sold has no proceeds, and a
 * field accepting one at purchase invites somebody to record the price they *hope* to get.
 */
export const buyTreatSchema = z.object({
  sectionId: objectId,
  brokerPartyId: objectId,
  safeId: objectId,
  title: z.string().trim().min(1).max(200),
  /**
   * **What is being bought**, in enough detail to recognise it again.
   *
   * `type` decides which details a form *asks* for (`ASSET_DETAIL_FIELDS`) — area and
   * location for a property or a plot, a plate and a description for a vehicle. All of them
   * are accepted on every type regardless: refusing a detail somebody genuinely has is a
   * worse failure than an unusual one being filled in, and none of this is ever summed or
   * posted.
   *
   * `area` is a **string**, not a number, and that is the one decision here worth defending.
   * The group works in metres and in dunam, and a bare number carrying its unit only in the
   * operator's head is wrong long before anybody checks it.
   */
  assetRef: z
    .object({
      type: enumOf(C.ASSET_TYPE).default(C.ASSET_TYPE.OTHER),
      label: z.string().trim().max(200).optional(),
      area: z.string().trim().max(60).optional(),
      location: z.string().trim().max(200).optional(),
      identifier: z.string().trim().max(60).optional(),
      /* A fixed choice, not free text — see `TITLE_DOC_TYPE`. Accepted on any type for the
         same reason every other detail is: refusing one somebody genuinely has is a worse
         failure than an unusual one being filled in. */
      titleType: enumOf(C.TITLE_DOC_TYPE).optional(),
      details: z.string().trim().max(500).optional(),
    })
    .optional(),
  buyDate: isoDate.optional(),
  buyAmount: positiveMoney,
  /**
   * Who it was bought from — a **name**, not a party.
   *
   * A treat is bought for cash (Dr Trading Asset, Cr Safe — docs/10 §3.1), so the seller is
   * never posted to: no payable is raised against them and no statement is ever filtered to
   * them. Requiring a `Party` therefore made an operator create a vendor record for a man
   * they will deal with once, to satisfy a field that only ever gets read back as a label.
   * `sellerPartyId` stays for the case where the counterparty *is* on file.
   */
  sellerName: z.string().trim().max(200).optional(),
  sellerPartyId: optionalObjectId,
  fxRateMicro: z.number().int().positive().optional(),
  commissionPlanId: optionalObjectId,
  notes: z.string().trim().max(2000).optional(),
  attachments: attachmentRefs,
});

export const sellTreatSchema = z.object({
  safeId: objectId,
  sellDate: isoDate.optional(),
  sellAmount: positiveMoney,
  /** Who bought it — a name, for the same reason `sellerName` is one on the buy. */
  buyerName: z.string().trim().max(200).optional(),
  buyerPartyId: optionalObjectId,
  /** The company's share **of the profit**. Falls back to the broker's agreed rate. */
  companyBps: percentBps.optional(),
  note: z.string().trim().max(1000).optional(),
});

export const listTreatsQuery = listQueryBase.extend({
  brokerPartyId: optionalObjectId,
  status: z.union([z.string(), z.array(z.string())]).optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
});

// ---------------------------------------------------------------------------
// Real Estate treats — the company's own money (docs/10 §4.2a)
// ---------------------------------------------------------------------------

/**
 * **What identifies the property**, in the terms the section actually uses.
 *
 * `propertyNo` and `quarter` are the address as it is spoken locally — the property's
 * number and the quarter (گەڕەک / محلة) it stands in — not a foreign key. Real Estate runs
 * no property register (docs/10 §4.2); the register moved to Construction, and requiring a
 * `Property` row here would mean creating one in another section's module before anybody
 * could record a purchase.
 *
 * Both are free text and both are on the row, because together they are how a treat is
 * recognised on the report.
 */
const propertyRefSchema = z.object({
  propertyNo: z.string().trim().min(1).max(60),
  quarter: z.string().trim().min(1).max(120),
});

/**
 * How a treat is **described**, as opposed to how it is identified.
 *
 * Kept apart from `propertyRefSchema` because only the treat register needs it: a tenancy
 * points at the same property and has no use for its area. Both optional, both free text —
 * `area` is written the way it is said ("200 m²", "2 dunam"), since the group works in more
 * than one unit and nothing here is ever parsed or totalled.
 */
const propertyDetailSchema = z.object({
  area: z.string().trim().max(60).optional(),
  address: z.string().trim().max(300).optional(),
});

/**
 * Buy a property with the company's cash. The treat opens here.
 *
 * No `sellPrice` — a treat that has not sold has no proceeds, and a field accepting one at
 * purchase invites somebody to record the price they *hope* to get. `buyerName` is allowed
 * though, because the section often buys with a buyer already lined up; it is a note until
 * the sale confirms it.
 */
export const buyPropertyTreatSchema = propertyRefSchema.merge(propertyDetailSchema).extend({
  sectionId: objectId,
  safeId: objectId,
  buyDate: isoDate.optional(),
  buyPrice: positiveMoney,
  /** Who it was bought from — a name, never a party: the purchase is settled in cash. */
  sellerName: z.string().trim().max(200).optional(),
  /** Who it is expected to go to. Optional, and overridable at the sale. */
  buyerName: z.string().trim().max(200).optional(),
  fxRateMicro: z.number().int().positive().optional(),
  notes: z.string().trim().max(2000).optional(),
  attachments: attachmentRefs,
});

export const sellPropertyTreatSchema = z.object({
  safeId: objectId,
  sellDate: isoDate.optional(),
  sellPrice: positiveMoney,
  /** Who bought it. Overwrites whatever the purchase recorded as expected. */
  buyerName: z.string().trim().max(200).optional(),
  note: z.string().trim().max(1000).optional(),
});

export const listPropertyTreatsQuery = listQueryBase.extend({
  status: z.union([z.string(), z.array(z.string())]).optional(),
  quarter: z.string().trim().max(120).optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
});

/* The treats *report* query is declared with the other report queries below, because it
   extends `reportQuery` and a `const` cannot be read before its own line. */

// ---------------------------------------------------------------------------
// Real Estate tenancies (docs/10 §4.2c)
// ---------------------------------------------------------------------------

/**
 * A tenancy — who rents which property, for how long, and on what rhythm.
 *
 * Reuses `propertyRefSchema` above, and for the same reason it exists: Real Estate names a
 * property rather than pointing at one, because §4.1 moved the register to Construction.
 *
 * `endDate` is validated against `startDate` **here**, so a term that runs backwards is
 * refused before the service builds anything. Whether the term is long enough to hold a
 * whole payment period is *not* checked here — that depends on `everyNMonths` and on
 * clamping month arithmetic, and it lives with the schedule builder that has to be right
 * about it anyway.
 */
export const createTenancySchema = propertyRefSchema
  .extend({
    sectionId: objectId,
    tenantPartyId: objectId,
    startDate: isoDate,
    endDate: isoDate,
    /** Rent falls due every N months — a number, not `LEASE_FREQUENCY` (docs/10 §4.2b). */
    everyNMonths: z.number().int().min(1).max(12).default(1),
    /** Per period, not per year. */
    rentAmount: positiveMoney,
    /**
     * The insurance held against the property (تەئمینات) — a **term**, not a payment.
     * Collecting it is a `deposit` invoice; this is what was agreed. See the model.
     */
    depositAmount: nonNegativeMoney.optional(),
    notes: z.string().trim().max(2000).optional(),
    attachments: attachmentRefs,
  })
  .superRefine((value, ctx) => {
    if (new Date(value.endDate) <= new Date(value.startDate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'a tenancy must end after it starts',
      });
    }
  });

/**
 * Amend one.
 *
 * Not `createTenancySchema.partial()`: that schema carries a cross-field refinement which
 * cannot fire on a payload that supplies only one of the two dates, and `.partial()` on a
 * `ZodEffects` is not available anyway. The service re-checks the pair against whatever is
 * already stored, which is the only place both halves are known.
 */
export const updateTenancySchema = z.object({
  /** Correctable only while nothing has been billed — the invoices name the tenant. */
  tenantPartyId: objectId.optional(),
  propertyNo: z.string().trim().min(1).max(60).optional(),
  quarter: z.string().trim().min(1).max(120).optional(),
  startDate: isoDate.optional(),
  endDate: isoDate.optional(),
  everyNMonths: z.number().int().min(1).max(12).optional(),
  rentAmount: positiveMoney.optional(),
  depositAmount: nonNegativeMoney.optional(),
  notes: z.string().trim().max(2000).optional(),
  attachments: attachmentRefs.optional(),
});

/** Bill one period — he paid, raise his invoice. */
export const invoiceTenancyPeriodSchema = z.object({
  date: isoDate.optional(),
  /** Overrides the generated "Rent 3/12 — 12 · Sarchnar" line description. */
  description: z.string().trim().max(500).optional(),
  /** Who earned commission on this month's rent, if anybody did. */
  earnedByPartyIds: z.array(objectId).max(10).optional(),
  notes: z.string().trim().max(2000).optional(),
});

/**
 * **Site expenses** (docs/10 §20) — the Properties module's own expense, entered and final
 * in one step.
 *
 * Deliberately *not* `createExpenseSchema.extend(...)`. That schema belongs to the Expenses
 * module and describes a document that is drafted, submitted and posted; this one describes
 * a document that exists in one state. Sharing it would tie two flows together at exactly
 * the point the task asked to keep them apart, and every field the other flow adds would
 * arrive here unasked.
 *
 * `propertyId` is required and carries the section with it — a site knows whose books it
 * belongs to, so the form never asks. `paymentMode` is absent for the same reason: money
 * spent on a build comes out of a safe.
 */
export const createPropertyExpenseSchema = z.object({
  propertyId: objectId,
  date: isoDate,
  description: z.string().trim().min(1).max(400),
  amount: positiveMoney,
  currency: currencyCode.optional(),
  /** Falls back to the section's `expenseDefault` when the form leaves it out. */
  categoryAccountId: optionalObjectId,
  safeId: optionalObjectId,
  partyId: optionalObjectId,
  spentByPartyId: optionalObjectId,
  notes: z.string().trim().max(2000).optional(),
  /**
   * The receipt, and it is **optional by design**. The file is already in storage by the
   * time this arrives — the client presigns, PUTs and confirms first (**I11**) — so this is
   * the id of a confirmed attachment, never the bytes.
   */
  attachmentId: optionalObjectId,
  /**
   * The idempotency key a double-submitted form repeats. Bounded and opaque: the server only
   * ever compares it, so its shape is the client's business.
   */
  clientRef: z.string().trim().min(8).max(64).optional(),
});

/**
 * Amending one. Every field optional — the caller sends what changed.
 *
 * `attachmentId` has **three** meanings and the schema has to keep them apart: an id
 * replaces the receipt, `null` removes it, and omitting it carries the existing file onto
 * the corrected document. `.nullable().optional()` is what distinguishes "cleared" from "not
 * mentioned"; collapsing them would drop a receipt every time somebody fixed an amount.
 */
export const updatePropertyExpenseSchema = z
  .object({
    propertyId: optionalObjectId,
    date: isoDate.optional(),
    description: z.string().trim().min(1).max(400).optional(),
    amount: positiveMoney.optional(),
    currency: currencyCode.optional(),
    categoryAccountId: optionalObjectId,
    safeId: optionalObjectId,
    partyId: optionalObjectId,
    spentByPartyId: optionalObjectId,
    notes: z.string().trim().max(2000).optional(),
    attachmentId: objectId.nullable().optional(),
    reason: z.string().trim().max(500).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'nothing to change');

export const listPropertyExpensesQuery = listQueryBase.extend({
  propertyId: optionalObjectId,
});

// ---------------------------------------------------------------------------
// Cleaner — orders and the expenses spent on them (docs/10 §50)
// ---------------------------------------------------------------------------

/**
 * **A Cleaner order** — paid on entry, so this is the whole document; there is no update.
 *
 * `meters` is optional because a car or a water tank is priced per job, and a plain number
 * because it is a measurement rather than money (**I1**). `chargeSectionId` is where the
 * order's *expenses* are booked; leaving it out means Cleaner itself.
 */
export const createCleaningOrderSchema = z.object({
  sectionId: objectId,
  date: isoDate.optional(),
  type: z.enum(C.CLEANING_ORDER_TYPES),
  meters: z.number().nonnegative().max(1_000_000).optional(),
  amount: positiveMoney,
  fxRateMicro: z.number().int().positive().optional(),
  safeId: optionalObjectId,
  customerPartyId: optionalObjectId,
  address: z.string().trim().max(300).optional(),
  chargeSectionId: optionalObjectId,
  notes: z.string().trim().max(2000).optional(),
  clientRef: z.string().trim().min(8).max(64).optional(),
});

/**
 * **An administrator's correction to a posted order** (§57). Every field optional — the
 * caller sends what changed — and `sectionId` is absent on purpose: moving an order to
 * another section would be a different section's order wearing this one's number.
 */
export const updateCleaningOrderSchema = z
  .object({
    date: isoDate.optional(),
    type: z.enum(C.CLEANING_ORDER_TYPES).optional(),
    meters: z.number().nonnegative().max(1_000_000).nullable().optional(),
    amount: positiveMoney.optional(),
    fxRateMicro: z.number().int().positive().optional(),
    safeId: optionalObjectId,
    customerPartyId: objectId.nullable().optional(),
    address: z.string().trim().max(300).nullable().optional(),
    chargeSectionId: optionalObjectId,
    notes: z.string().trim().max(2000).nullable().optional(),
    note: z.string().trim().max(500).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'nothing to change');

export const listCleaningOrdersQuery = listQueryBase.extend({
  status: z.union([z.string(), z.array(z.string())]).optional(),
  type: z.enum(C.CLEANING_ORDER_TYPES).optional(),
  customerPartyId: optionalObjectId,
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
});

/**
 * **An expense on an order** — the order carries the section, so the form never asks.
 * The same shape as a site expense, minus the property: final on arrival, cash out of a
 * safe, the receipt optional.
 */
export const createCleaningExpenseSchema = z
  .object({
    /** The job it was spent on. Leave it out for a running cost of the section (§52). */
    orderId: optionalObjectId,
    /** The section a cost with no order belongs to — ignored when an order names its own. */
    sectionId: optionalObjectId,
    date: isoDate,
    description: z.string().trim().min(1).max(400),
    amount: positiveMoney,
    currency: currencyCode.optional(),
    fxRateMicro: z.number().int().positive().optional(),
    /** Falls back to the charge section's `expenseDefault`. */
    categoryAccountId: optionalObjectId,
    safeId: optionalObjectId,
    partyId: optionalObjectId,
    spentByPartyId: optionalObjectId,
    notes: z.string().trim().max(2000).optional(),
    attachmentId: optionalObjectId,
    clientRef: z.string().trim().min(8).max(64).optional(),
  })
  .refine((v) => v.orderId || v.sectionId, {
    message: 'name the order, or the section the cost belongs to',
    path: ['orderId'],
  });

export const listCleaningExpensesQuery = listQueryBase.extend({
  orderId: optionalObjectId,
  /** The costs of one customer's jobs — their own report (§56). */
  customerPartyId: optionalObjectId,
  /** `order` for a job's costs, `section` for the section's own (§57). */
  kind: z.enum(['order', 'section']).optional(),
  /** Everything in this section's books — the Cleaner Expenses screen. */
  sectionId: optionalObjectId,
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
});

/** The Cleaner report and the customer report share one window (§52). */
export const cleanerReportQuery = z.object({
  sectionId: optionalObjectId,
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  q: z.string().trim().max(200).optional(),
});

export const listTenanciesQuery = listQueryBase.extend({
  status: z.union([z.string(), z.array(z.string())]).optional(),
  tenantPartyId: optionalObjectId,
  quarter: z.string().trim().max(120).optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
});

/** `seq` arrives on the path, so it is a string until it is coerced. */
export const tenancyPeriodParam = z.object({
  id: objectId,
  seq: z.coerce.number().int().min(1),
});

export const brokerStatementQuery = listQueryBase.extend({
  brokerPartyId: objectId,
  direction: enumOf(C.BROKER_MOVEMENT).optional(),
  currency: currencyCode.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
});

export const dealProfitabilityQuery = z.object({
  sectionId: optionalObjectId,
  brokerPartyId: optionalObjectId,
  stage: z.union([z.string(), z.array(z.string())]).optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
});

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export const reportQuery = z.object({
  sectionId: optionalObjectId,
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  currency: currencyCode.optional(),
  compare: boolish.default(false),
  format: z.enum(['json', 'xlsx', 'pdf']).default('json'),
});

export const trialBalanceQuery = reportQuery.extend({
  /** Accounts that moved and netted back to zero are hidden unless asked for. */
  includeZeroBalances: boolish.default(false),
});

/**
 * The financial statements (Phase 12).
 *
 * `compareFrom`/`compareTo` are a second window reported beside the first. They are two
 * explicit dates rather than a `compare: 'previousPeriod'` flag, because "the previous
 * period" is a different length depending on whether the reader picked a month, a quarter
 * or an arbitrary range — and a comparison column that silently changed length would be
 * read as like-for-like.
 */
export const incomeStatementQuery = reportQuery.extend({
  compareFrom: isoDate.optional(),
  compareTo: isoDate.optional(),
  /** The account-level detail behind each bucket. Off by default: it is a much larger payload. */
  includeAccounts: boolish.default(false),
});

/**
 * **The General Report** — every section on one page (docs/10 §4.3).
 *
 * `reportQuery` unchanged: the same window, the same optional `sectionId`, the same export
 * `format`. Narrowing it to one section is allowed and is not a contradiction — a report
 * that shows all sections by default is still the right shape to ask a single one of.
 */
export const generalReportQuery = reportQuery;

/**
 * **The activity register** — everything created in the system (docs/10 §16).
 *
 * `types` is a comma-separated list rather than a repeated query parameter or a JSON array,
 * because it has to survive three round trips unchanged: the URL the operator bookmarks, the
 * `params` blob a queued export carries, and the saved view. A repeated parameter arrives as
 * a string when there is one value and an array when there are two, which every consumer then
 * has to normalise; one string is one shape.
 *
 * Deliberately **not** an enum of the type keys. The register's source list lives on the
 * server, where the models are, and duplicating it here would mean adding a document type in
 * two places — with the failure being a filter the API rejects for a type it can perfectly
 * well produce. Unknown keys simply match nothing.
 */
export const activityQuery = reportQuery.extend({
  types: z
    .string()
    .max(500)
    .regex(/^[A-Za-z,]*$/, 'types is a comma-separated list of document types')
    .optional(),
  /** Matches the document number — what an operator retypes off a piece of paper. */
  q: z.string().trim().max(200).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const balanceSheetQuery = reportQuery.extend({
  /** A position is at a moment, not over a window — `dateFrom` means nothing here. */
  asOf: isoDate.optional(),
  includeZeroBalances: boolish.default(false),
});

export const cashFlowQuery = reportQuery;

export const generalLedgerQuery = reportQuery.extend({
  accountId: optionalObjectId,
  partyId: optionalObjectId,
  sourceType: enumOf(C.JOURNAL_SOURCE).optional(),
  sourceId: optionalObjectId,
  entryNo: z.string().trim().min(1).max(40).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

/** One account, with the balance it opened on and the balance it closed on. */
export const accountStatementQuery = generalLedgerQuery.extend({
  accountId: objectId,
});

/**
 * The Safe report (docs/10 §2.2) — the box, its per-currency totals, what each section
 * put in and took out, and a drillable feed.
 *
 * `sectionId` and `currency` filter the **feed only**; the totals stay whole, because the
 * point of the panel above the feed is what the group holds, not what the filter shows.
 */
export const safeReportQuery = reportQuery.extend({
  feedLimit: z.coerce.number().int().min(1).max(200).default(50),
  sectionId: optionalObjectId,
  currency: currencyCode.optional(),
});

export const safeStatementQuery = reportQuery.extend({
  safeId: objectId,
  /** Which compartment (docs/02 §3). Omitted means the safe's primary currency. */
  currency: currencyCode.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

export const expenseByCategoryQuery = reportQuery.extend({
  paymentMode: enumOf(C.EXPENSE_PAYMENT_MODE).optional(),
});

/**
 * **The treats report** — every property bought and sold on the company's own account,
 * with what each one made (docs/10 §4.2a).
 *
 * Dated on the **purchase**, like the list, so a treat appears in the range in which the
 * money left rather than only once it sells. A range that reported on the sale date would
 * hide every open position, which is the half of the register that is still at risk.
 */
export const propertyTreatReportQuery = reportQuery.extend({
  status: z.union([z.string(), z.array(z.string())]).optional(),
  quarter: z.string().trim().max(120).optional(),
});

/** Every invoice a section raised or received — both directions unless `kind` narrows it. */
export const invoiceRegisterQuery = reportQuery.extend({
  kind: enumOf(C.INVOICE_KIND).optional(),
  /** Narrow to one of rent / deposit / commission / receipt / payment (docs/10 §1). */
  type: enumOf(C.INVOICE_TYPE).optional(),
  status: enumOf(C.DOC_STATUS).optional(),
  partyId: optionalObjectId,
});

/**
 * Report exports (Phase 12).
 *
 * `params` is a passthrough object rather than a union of every report's query schema.
 * The worker hands it to the report service, which validates its own inputs, and a
 * discriminated union here would have to be extended in step with every report or
 * silently drop the filter that mattered. `.max()` on the key count is the guard that
 * matters: the row is stored, so an unbounded object is a write amplification vector.
 */
export const createReportExportSchema = z.object({
  reportKey: z.string().trim().min(1).max(60),
  /**
   * `pdf` is served by headless Chromium and needs the worker (docs/10 §6). Asking for one
   * on a deployment without it fails at request time with `PDF_UNAVAILABLE`, rather than
   * leaving a row pending for a renderer that is never going to run.
   */
  format: z.enum(['xlsx', 'pdf']).default('xlsx'),
  params: z
    .record(z.union([z.string(), z.number(), z.boolean()]))
    .refine((v) => Object.keys(v).length <= 30, 'too many filters')
    .default({}),
});

export const listReportExportsQuery = paginationQuery;

/**
 * A saved view — a named set of filters for one report.
 *
 * `isShared` makes it visible to everyone in the section rather than only its author. It
 * is a share, never a grant: a colleague opening a shared view still gets whatever the
 * report shows *them*, which for a different section is different figures entirely.
 */
export const createSavedViewSchema = z.object({
  name: z.string().trim().min(1).max(80),
  reportKey: z.string().trim().min(1).max(60),
  params: z
    .record(z.union([z.string(), z.number(), z.boolean()]))
    .refine((v) => Object.keys(v).length <= 30, 'too many filters')
    .default({}),
  isShared: boolish.default(false),
  isDefault: boolish.default(false),
});

export const updateSavedViewSchema = createSavedViewSchema.partial().strict();

export const listSavedViewsQuery = paginationQuery.extend({
  reportKey: z.string().trim().min(1).max(60).optional(),
});

export const agingQuery = reportQuery.extend({
  kind: enumOf(C.INVOICE_KIND).default(C.INVOICE_KIND.INCOME),
  asOf: isoDate.optional(),
  partyId: optionalObjectId,
});

export { idParam, workflowActionSchema, paginationQuery, listQueryBase };
